<template>
  <fragment>
    <div
      :class="[
        'question-form__slider-wrapper',
        'mt-3',
        'slider-container',
        { 'required-question': question.required },
      ]"
    >
      <b-row>
        <b-col cols="12">
          <b-alert :show="!!totalError" variant="danger">{{
            totalError
          }}</b-alert>
        </b-col>
      </b-row>
      <b-row
        v-if="!isPreview && question.calculate_totals && isPercentageQuestion"
        class="border-bottom pb-2 d-flex justify-content-between mb-3"
      >
        <b-col
          cols="auto"
          class="d-flex justify-content-start font-weight-bold font_size_1000rem"
        >
          <span>
            Total:
            <span :class="['totals', { invalid: !isTotalValid }]">
              {{ totalString }}
            </span>
          </span>
        </b-col>
        <b-col
          v-if="!isTotalValid"
          cols="auto"
          class="d-flex justify-content-end totals invalid font-weight-bold font_size_1000rem"
        >
          <span>
            Total must be: 100%
          </span>
        </b-col>
      </b-row>
      <b-row v-if="question.is_unknown_enabled">
        <b-col>
          <b-form-checkbox
            inline
            class="v3_control_checkbox my-3"
            :checked="isUnknownChecked"
            @change="onSetUnknown($event)"
          >
            {{ question.unknown_option_label || 'I don’t know the value' }}
          </b-form-checkbox>
        </b-col>
      </b-row>
      <b-row v-for="(v, n) in visibleVariables" :key="n">
        <b-col
          :class="{
            'border-bottom': n !== visibleVariables.length - 1,
            'mb-3': n !== visibleVariables.length - 1,
            'pb-2': n !== visibleVariables.length - 1,
          }"
        >
          <b-alert
            :show="!!variablesErrorsByOwnCondition[v.id]"
            variant="danger"
          >
            {{ variablesErrorsByOwnCondition[v.id] }}
          </b-alert>
          <div
            v-if="Array.isArray(variableErrorsByOtherVariablesConditions[v.id])"
          >
            <b-alert
              v-for="error in variableErrorsByOtherVariablesConditions[v.id]"
              :key="error"
              :show="!!error"
              variant="danger"
            >
              {{ error }}
            </b-alert>
          </div>
          <div
            v-if="Array.isArray(variableErrorsByOtherQuestionsConditions[v.id])"
          >
            <b-alert
              v-for="error in variableErrorsByOtherQuestionsConditions[v.id]"
              :key="error"
              :show="!!error"
              variant="danger"
            >
              {{ error }}
            </b-alert>
          </div>
          <div
            v-if="visibleVariables.length > 1"
            class="font-weight-bold font_size_1000rem v2_color_black"
          >
            {{ v.input_label }}
          </div>

          <slider-input
            :disabled="isDisable || !!isUnknownChecked"
            :initial-value="
              isPreview ? previewSliderResponse.value : +v.response.value
            "
            :variable="v"
            :is-set-to-min="v.response && !!v.response.is_set_min"
            :is-unknown="!!isUnknownChecked"
            :is-declined="isDeclined"
            :slider-number-input-hidden="isUnknownChecked"
            @set-value="sliderUpdated(v, $event)"
          />

          <b-form-checkbox
            inline
            v-if="
              (!question.calculate_totals || !isPercentageQuestion) &&
              !isMinValueAboveZero(v.min) && !v.is_set_zero_hidden
            "
            class="v3_control_checkbox my-3"
            :checked="v.response && !!v.response.is_set_min"
            :disabled="!!isUnknownChecked"
            @change="onSetMin(v.id || v.uid, $event)"
          >
            {{ v.set_zero_label || defaultSetToMinLabel }}
          </b-form-checkbox>
        </b-col>
      </b-row>
    </div>
  </fragment>
</template>

<script>
import sumBy from 'lodash/sumBy';
import toNumber from 'lodash/toNumber';
import SliderInput from '@/components/study/form/SliderInput';
import { SETTINGS_KEYS } from '@/store';
import {
  sliderQuestionConditionValidation,
  sliderVariableConditionValidation,
  validateVarResponseByOtherQuestionCondition,
  validateVarResponseByOtherVarCondition,
} from '@/helpers/questionnaireHelper';

export default {
  name: 'StudyFormInputSlider',
  components: {
    SliderInput,
  },
  props: {
    question: {
      type: Object,
      required: true,
    },
    variables: {
      type: Array,
      required: true,
    },
    visibleVariables: {
      type: Array,
      required: true,
    },
    isDisable: {
      type: Boolean,
      default: false,
    },
    isPreview: {
      type: Boolean,
      default: false,
    },
    isDeclined: {
      type: Boolean,
      default: false,
    },
    allVariables: Array,
    allQuestions: Array,
  },
  data: function () {
    return {
      previewSliderResponse: { value: 0 },
      variablesErrorsByOwnCondition: {},
      variableErrorsByOtherVariablesConditions: {},
      variableErrorsByOtherQuestionsConditions: {},
      totalError: null,
    };
  },
  created: function () {
    if (this.isQuestionUnanswered || this.isDeclined || this.isUnknownChecked) {
      return;
    }
    const isCalculateTotalQuestion = this.question.calculate_totals === 1;
    if (isCalculateTotalQuestion) {
      this.checkQuestionTotalConditionValidation();
    }
    this.visibleVariables.forEach((variable) => {
      const val = ((variable || {}).response || {}).value;
      this.checkConditionValidationByOtherVariables(variable, val);
      this.checkConditionValidationByOtherQuestions(
        variable,
        val,
        this.allQuestions
      );
      if (!isCalculateTotalQuestion)
        this.checkVariableConditionValidation(variable, val);
    });
  },
  computed: {
    isPercentageQuestion: function () {
      const suffix = this.variables[0].suffix || '';
      return suffix.trim() === '%';
    },
    isQuestionUnanswered: function () {
      return this.visibleVariables.every(
        (v) =>
          !v.response ||
          v.response.value === null ||
          v.response.value === undefined
      );
    },
    isTotalValid: function () {
      if (!this.question.calculate_totals || !this.variables[0]) {
        return true;
      }
      return !this.isPercentageQuestion || this.total === 100;
    },
    isQuestionValid: function () {
      return (
        this.isTotalValid &&
        !this.totalError &&
        !Object.keys(this.variablesErrorsByOwnCondition).length &&
        !Object.keys(this.variableErrorsByOtherVariablesConditions).length &&
        !Object.keys(this.variableErrorsByOtherQuestionsConditions).length
      );
    },
    total: function () {
      if (!this.question.calculate_totals) return 0;
      return sumBy(this.visibleVariables, (v) => {
        const val = v.response.value;
        return val ? toNumber(val) : 0;
      });
    },
    totalString: function () {
      if (!this.question.calculate_totals || !this.variables[0]) {
        return '';
      }
      const prefix = this.variables[0].prefix ? this.variables[0].prefix : '';
      const suffix = this.variables[0].suffix || '';
      return (
        prefix + parseFloat(this.total).toLocaleString('en') + suffix.trim()
      );
    },
    defaultSetToMinLabel: function () {
      const settingLabel = this.$store.getters.getSetting(
        SETTINGS_KEYS.DEFAULT_SET_ZERO_LABEL
      );
      return settingLabel || 'Set value to zero.';
    },
    isUnknownChecked: function () {
      return this.variables.every(
        (v) => v.response && v.response.is_unknown === 1
      );
    },
  },
  methods: {
    sliderUpdated: function (variable, val) {
      if (!this.isPreview) {
        this.$emit('slider-updated', { uid: variable.id || variable.uid, val });
        this.$nextTick(() => {
          if (this.question.calculate_totals === 1) {
            this.checkQuestionTotalConditionValidation();
          } else {
            this.checkVariableConditionValidation(variable, val);
          }
          this.checkConditionValidationByOtherVariables(variable, val);
          this.checkConditionValidationByOtherQuestions(variable, val);
        });
      } else {
        this.previewSliderResponse.value = val;
      }
    },
    onSetMin: function (uid, val) {
      this.$emit('set-min-updated', { uid, val });
    },
    onSetUnknown: function (val) {
      this.$emit('slider-set-unknown', val ? 1 : 0);
      if (val) this.resetErrors();
    },
    isMinValueAboveZero: function (minValue) {
      return !!parseInt(minValue) && parseInt(minValue) > 0;
    },
    checkQuestionTotalConditionValidation: function () {
      const { isValid, error = null } = sliderQuestionConditionValidation(
        this.question,
        this.visibleVariables,
        this.allVariables
      );
      this.totalError = isValid ? null : error;
    },
    checkVariableConditionValidation: function (variable, val) {
      const { isValid, error = null } = sliderVariableConditionValidation(
        variable,
        val,
        this.allVariables
      );
      if (!isValid) {
        this.$set(
          this.variablesErrorsByOwnCondition,
          variable.id || variable.uid,
          error
        );
      } else {
        this.$delete(
          this.variablesErrorsByOwnCondition,
          variable.id || variable.uid
        );
      }
    },
    checkConditionValidationByOtherVariables: function (variable, val) {
      const { isValid, errors = [] } = validateVarResponseByOtherVarCondition(
        variable,
        val,
        this.allVariables
      );
      if (!isValid) {
        this.$set(
          this.variableErrorsByOtherVariablesConditions,
          variable.id || variable.uid,
          errors
        );
      } else {
        this.$delete(
          this.variableErrorsByOtherVariablesConditions,
          variable.id || variable.uid
        );
      }
    },
    checkConditionValidationByOtherQuestions: function (variable, val) {
      const {
        isValid,
        errors = [],
      } = validateVarResponseByOtherQuestionCondition(
        variable,
        val,
        this.allQuestions
      );
      if (!isValid) {
        this.$set(
          this.variableErrorsByOtherQuestionsConditions,
          variable.id || variable.uid,
          errors
        );
      } else {
        this.$delete(
          this.variableErrorsByOtherQuestionsConditions,
          variable.id || variable.uid
        );
      }
    },
    resetErrors: function () {
      this.totalError = null;
      this.variablesErrorsByOwnCondition = {};
      this.variableErrorsByOtherVariablesConditions = {};
      this.variableErrorsByOtherQuestionsConditions = {};
    },
  },
  watch: {
    isQuestionValid: {
      handler: function (newVal) {
        if (this.isQuestionUnanswered || this.isPreview) {
          return;
        }
        this.$emit('valid-updated', newVal);
      },
      immediate: true,
    },
    isQuestionUnanswered: function (newVal) {
      if (!newVal) {
        this.$emit('valid-updated', this.isQuestionValid);
      }
    },
    isDeclined: function (newVal, prevVal) {
      if (newVal && !prevVal) {
        this.resetErrors();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
// include this in any Vue component where you want to reference vars or mixins
@import '../../../../../sass/variables_mixins.scss';
.totals {
  color: $phairify_color_green;
  &.invalid {
    color: $phairify_color_red;
  }
}
.slider-container {
  border: 1px solid #AAB0B3;
  padding: 25px;
}
</style>
